import React, { useState, useEffect } from 'react';
import BoldText from '../components/BoldText';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import useDetailBreadcrumbs from '../hooks/useDetailBreadcrumbs';
import InlineField from '../components/InlineField';
import { useCapturePageContext } from '../capture/CaptureContext';
import { useAuth } from '../context/AuthContext';
import {
  getOpportunity,
  updateOpportunity,
  deleteOpportunity,
  getOpportunityStages,
  getUsers,
  getProducts,
  getRepFirms,
  createActivity,
  createTask,
  createQuote,
  getContacts,
  createContact,
  getCompanySettings,
  optional,
  patchOpportunity,
} from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  ArrowLeft,
  Trash2,
  Plus,
  Edit,
  Phone,
  Mail,
  Calendar,
  FileText,
  User,
  Target,
  MessageSquare,
  DollarSign,
  AlertTriangle,
  Check,
  X,
} from 'lucide-react';
import { AskAIButton } from '../components/ai';
import { formatCurrency } from '../utils/formatCurrency';
import EmptyValue from '../components/EmptyValue';
import './AccountDetail/AccountDetail.css';

const OPPORTUNITY_TABS = ['overview', 'products', 'quotes', 'orders', 'activities', 'tasks'];

function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, canDo, user, isModuleVisible, canOverridePrice, getMaxDiscountPct } = useAuth();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Reference data
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [repFirms, setRepFirms] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Modals
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // Form data
  const [formData, setFormData] = useState({});
  const [activityForm, setActivityForm] = useState({ type: 'Call', description: '', contact_id: '' });
  const [taskForm, setTaskForm] = useState({ title: '', due_date: '', assigned_to: '', contact_id: '' });

  // Tabs — in the URL (?tab=…) so refresh, Back and shared links keep them.
  const [activeTab, setActiveTab] = useTabParam('overview', OPPORTUNITY_TABS);
  useDetailBreadcrumbs(opportunity && opportunity.name ? [
    ...(opportunity.account_id && opportunity.account_name
      ? [{ label: 'Accounts', path: '/accounts' }, { label: opportunity.account_name, path: `/accounts/${opportunity.account_id}?tab=opportunities` }]
      : [{ label: 'Opportunities', path: '/opportunities' }]),
    { label: opportunity.name },
  ] : null);
  useCapturePageContext(opportunity && opportunity.account_id ? { account_id: opportunity.account_id, account_name: opportunity.account_name, opportunity_id: opportunity.id, opportunity_name: opportunity.name } : null);

  // Inline product editing
  const [isEditingProducts, setIsEditingProducts] = useState(false);
  const [inlineLineItems, setInlineLineItems] = useState([]);
  const [productsSaving, setProductsSaving] = useState(false);

  // Overview edit mode
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '', job_title: '' });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      // Only the opportunity itself is required; the reference lists may be
      // module-restricted for this user and must not block the page.
      const [oppRes, stagesRes, usersRes, productsRes, repFirmsRes] = await Promise.all([
        getOpportunity(id),
        optional(getOpportunityStages()),
        optional(getUsers()),
        optional(getProducts()),
        optional(getRepFirms()),
      ]);
      setOpportunity(oppRes.data);
      setFormData({
        ...oppRes.data,
        line_items: oppRes.data.line_items || [],
      });
      setStages(stagesRes.data);
      setUsers(usersRes.data.filter(u => u.active));
      setProducts(productsRes.data.filter(p => p.active));
      setRepFirms(repFirmsRes.data);

      // Load contacts for the account
      if (oppRes.data.account_id) {
        const contactsRes = await optional(getContacts({ account_id: oppRes.data.account_id }));
        setContacts(contactsRes.data.filter(c => c.active));
      }
    } catch (err) {
      setError('Failed to load opportunity');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateOpportunity(id, formData);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update opportunity');
    }
  };

  // Overview edit mode helpers

  // Contact creation
  const openNewContactModal = () => {
    setNewContactForm({ first_name: '', last_name: '', email: '', phone: '', job_title: '' });
    setShowNewContactModal(true);
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!opportunity.account_id) return;
    try {
      const res = await createContact({ ...newContactForm, account_id: opportunity.account_id });
      const contactsRes = await getContacts({ account_id: opportunity.account_id });
      setContacts(contactsRes.data.filter(c => c.active));
      await saveField('contact_id', res.data.id);
      setShowNewContactModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create contact');
    }
  };



  // One field, saved on its own, through the patch route: nothing else on
  // the record is touched.
  const saveField = async (field, value) => {
    await patchOpportunity(id, { [field]: value === '' ? null : value });
    await loadData();
  };


  const handleDelete = async () => {
    try {
      await deleteOpportunity(id);
      navigate('/opportunities');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete opportunity');
    }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    try {
      await createActivity({
        ...activityForm,
        account_id: opportunity.account_id,
        opportunity_id: id,
      });
      setShowActivityModal(false);
      setActivityForm({ type: 'Call', description: '', contact_id: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log activity');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await createTask({
        ...taskForm,
        account_id: opportunity.account_id,
        opportunity_id: id,
        assigned_to: taskForm.assigned_to || user.id,
      });
      setShowTaskModal(false);
      setTaskForm({ title: '', due_date: '', assigned_to: '', contact_id: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create action item');
    }
  };

  // Check if there's already an active (non-rejected) quote for this opportunity
  const activeQuote = opportunity?.quotes?.find(q => q.status !== 'Rejected');
  const hasActiveQuote = !!activeQuote;

  // Frozen-field logic: line items are editable only when no active quote in Sent/Accepted
  const frozenFieldsEditable = opportunity?.frozen_fields_editable !== false;

  const handleCreateQuote = () => {
    // One quote per opportunity — block if active quote exists
    if (hasActiveQuote) {
      setError(`This opportunity already has an active quote (${activeQuote.quote_number} - ${activeQuote.status}). Reject it first to create a new one.`);
      return;
    }

    const currentStage = stages.find(s => s.id === opportunity.stage_id);

    // Closed Lost — block entirely
    if (currentStage?.is_lost) {
      setError('Cannot create a quote for a lost opportunity.');
      return;
    }

    // Closed Won — block
    if (currentStage?.is_won) {
      setError('Cannot create a quote for a Closed Won opportunity.');
      return;
    }

    // Validate: must have line items with products and quantities
    if (!opportunity.line_items || opportunity.line_items.length === 0) {
      setError('Add at least one product with quantity before creating a quote.');
      return;
    }

    // If before Proposal stage, the backend will auto-advance to Proposal
    executeCreateQuote();
  };

  const executeCreateQuote = async () => {
    try {
      // Fetch company settings for default terms
      let defaultTerms = '';
      try {
        const settingsRes = await getCompanySettings();
        defaultTerms = settingsRes.data?.default_terms || 'All prices are valid for the period specified above. Payment terms are as stated. Shipping dates are estimates and subject to change.';
      } catch (e) {
        // Use fallback terms if settings fail
        defaultTerms = 'All prices are valid for the period specified above. Payment terms are as stated. Shipping dates are estimates and subject to change.';
      }

      // Auto-calculate expiration date (30 days from today)
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 30);

      const quoteData = {
        account_id: opportunity.account_id,
        opportunity_id: id,
        contact_id: opportunity.contact_id,
        owner_id: opportunity.owner_id,
        rep_firm_id: opportunity.rep_firm_id,
        status: 'Draft',
        expiration_date: expirationDate.toISOString().split('T')[0],
        date_issued: new Date().toISOString().split('T')[0],
        validity_period: '30 Days',
        payment_terms: 'Net 30',
        delivery_terms: 'FOB Origin',
        currency: 'USD',
        tax_rate: 0,
        shipping_cost: 0,
        standard_terms: defaultTerms,
        line_items: opportunity.line_items?.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          description: item.description,
          sku: item.sku || '',
          quantity: item.quantity,
          unit_price: item.unit_price,
          lead_time: item.lead_time || '',
          discount_type: item.discount_type || null,
          discount_value: item.discount_value || 0,
        })) || [],
      };

      const response = await createQuote(quoteData);
      navigate(`/quotes/${response.data.id}`, { state: { fromOpportunity: id, fromAccount: opportunity.account_id } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create quote');
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { product_id: '', product_name: '', description: '', sku: '', quantity: 1, unit_price: 0, discount_type: '', discount_value: 0 }],
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          if (field === 'product_id' && value) {
            const product = products.find(p => p.id === parseInt(value));
            if (product) {
              updated.product_name = product.name;
              updated.description = product.description || '';
              updated.sku = product.sku || '';
              updated.unit_price = product.price;
            }
          }
          // Clamp discount_value to max allowed
          if (field === 'discount_value' || field === 'discount_type') {
            const maxPct = getMaxDiscountPct();
            const dVal = updated.discount_value || 0;
            if (updated.discount_type === 'percentage' && dVal > maxPct) {
              updated.discount_value = maxPct;
            }
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  // Inline product editing helpers
  const startEditingProducts = () => {
    setInlineLineItems((opportunity.line_items || []).map(item => ({ ...item })));
    setIsEditingProducts(true);
  };

  const stopEditingProducts = () => {
    setIsEditingProducts(false);
    setInlineLineItems([]);
  };

  const addInlineLineItem = () => {
    if (!isEditingProducts) {
      startEditingProducts();
      setInlineLineItems(prev => [...prev, { product_id: '', product_name: '', description: '', sku: '', quantity: 1, unit_price: 0, discount_type: '', discount_value: 0 }]);
    } else {
      setInlineLineItems(prev => [...prev, { product_id: '', product_name: '', description: '', sku: '', quantity: 1, unit_price: 0, discount_type: '', discount_value: 0 }]);
    }
  };

  const updateInlineLineItem = (index, field, value) => {
    setInlineLineItems(prev => prev.map((item, i) => {
      if (i === index) {
        const updated = { ...item, [field]: value };
        if (field === 'product_id' && value) {
          const product = products.find(p => p.id === parseInt(value));
          if (product) {
            updated.product_name = product.name;
            updated.description = product.description || '';
            updated.sku = product.sku || '';
            updated.unit_price = product.price;
          }
        }
        if (field === 'discount_value' || field === 'discount_type') {
          const maxPct = getMaxDiscountPct();
          const dVal = updated.discount_value || 0;
          if (updated.discount_type === 'percentage' && dVal > maxPct) {
            updated.discount_value = maxPct;
          }
        }
        return updated;
      }
      return item;
    }));
  };

  const removeInlineLineItem = (index) => {
    setInlineLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveInlineProducts = async () => {
    setProductsSaving(true);
    try {
      await updateOpportunity(id, {
        ...opportunity,
        line_items: inlineLineItems,
      });
      setIsEditingProducts(false);
      setInlineLineItems([]);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save products');
    } finally {
      setProductsSaving(false);
    }
  };

  const calculateLineTotal = (item) => {
    const raw = (item.quantity || 0) * (item.unit_price || 0);
    if (item.discount_type === 'percentage' && item.discount_value) {
      return raw * (1 - item.discount_value / 100);
    }
    return raw;
  };

  const calculateTotal = (items) => {
    return (items || []).reduce((sum, item) => {
      const raw = (item.quantity || 0) * (item.unit_price || 0);
      let discounted = raw;
      if (item.discount_type === 'percentage' && item.discount_value) {
        discounted = raw * (1 - item.discount_value / 100);
      }
      return sum + Math.max(0, discounted);
    }, 0);
  };

  // formatCurrency imported from utils/formatCurrency

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!opportunity) {
    return <div className="alert alert-error">Opportunity not found</div>;
  }

  return (
    <div className="opportunity-detail">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button className="btn btn-icon" onClick={() => navigate(location.state?.fromAccount ? `/accounts/${location.state.fromAccount}` : '/opportunities')} aria-label="Go back">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title">
              {(
                <span>
                  <InlineField value={opportunity.name} onSave={(v) => saveField('name', v)} canEdit={canDo('opportunities', 'edit')} label="opportunity name" />
                </span>
              )}
            </h1>
            <div className="flex items-center gap-2">
              {opportunity.stage_name && (
                <span className={`badge ${opportunity.is_won ? 'badge-success' : opportunity.is_lost ? 'badge-danger' : 'badge-primary'}`}>
                  {opportunity.stage_name}
                </span>
              )}
              <span className="text-secondary">
                {isModuleVisible('accounts') ? (
                  <Link to={`/accounts/${opportunity.account_id}`}>{opportunity.account_name}</Link>
                ) : (
                  <span>{opportunity.account_name}</span>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="page-actions">
          {opportunity.id && opportunity.account_id && <AskAIButton name={opportunity.name} />}
          {isModuleVisible('activities') && canDo('activities', 'create') && (
            <button className="btn btn-secondary" onClick={() => setShowActivityModal(true)}>
              <MessageSquare size={18} />
              Log Activity
            </button>
          )}
          {isModuleVisible('tasks') && canDo('tasks', 'create') && (
            <button className="btn btn-secondary" onClick={() => setShowTaskModal(true)}>
              <Plus size={18} />
              Add Action Item
            </button>
          )}
          {isModuleVisible('quotes') && canDo('quotes', 'create') && (
            <button
              className="btn btn-secondary"
              onClick={handleCreateQuote}
              disabled={hasActiveQuote}
              title={hasActiveQuote ? `Active quote exists: ${activeQuote.quote_number}` : ''}
            >
              <FileText size={18} />
              Create Quote
            </button>
          )}
          {canDo('opportunities', 'delete') && (
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} aria-label="Delete opportunity">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products ({opportunity.line_items?.length || 0})
        </button>
        {isModuleVisible('quotes') && (
          <button
            className={`tab ${activeTab === 'quotes' ? 'active' : ''}`}
            onClick={() => setActiveTab('quotes')}
          >
            Quotes ({opportunity.quotes?.length || 0})
          </button>
        )}
        {isModuleVisible('orders') && (
          <button
            className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Orders ({opportunity.orders?.length || 0})
          </button>
        )}
        {isModuleVisible('activities') && (
          <button
            className={`tab ${activeTab === 'activities' ? 'active' : ''}`}
            onClick={() => setActiveTab('activities')}
          >
            Activities ({opportunity.activities?.length || 0})
          </button>
        )}
        {isModuleVisible('tasks') && (
          <button
            className={`tab ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
            Action Items ({opportunity.tasks?.length || 0})
          </button>
        )}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="detail-grid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Opportunity Details</h3>
              {canDo('opportunities', 'edit') && <span className="text-muted text-sm">Click a value to change it</span>}
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Account</span>
                <span className="detail-value">
                  {isModuleVisible('accounts') ? (
                    <Link to={`/accounts/${opportunity.account_id}`}>{opportunity.account_name}</Link>
                  ) : opportunity.account_name}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Contact</span>
                <span className="detail-value">
                  <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <InlineField
                      value={opportunity.contact_id ? String(opportunity.contact_id) : ''}
                      onSave={(v) => saveField('contact_id', v)}
                      canEdit={canDo('opportunities', 'edit')}
                      type="select"
                      options={[{ value: '', label: 'Not set' }, ...contacts.map((c) => ({ value: String(c.id), label: c.name + (c.job_title ? ` - ${c.job_title}` : '') }))]}
                      label="contact"
                      emptyText="Not set"
                      format={() => opportunity.contact_name || '—'}
                    />
                    {canDo('opportunities', 'edit') && opportunity.account_id && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={openNewContactModal}>+ New contact</button>
                    )}
                  </span>
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Owner</span>
                <span className="detail-value">
                  <InlineField
                    value={opportunity.owner_id ? String(opportunity.owner_id) : ''}
                    onSave={(v) => saveField('owner_id', v)}
                    canEdit={canDo('opportunities', 'edit')}
                    type="select"
                    options={[{ value: '', label: 'Unassigned' }, ...users.map((u) => ({ value: String(u.id), label: u.name }))]}
                    label="owner"
                    emptyText="Unassigned"
                  />
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Rep Firm</span>
                <span className="detail-value">
                  <InlineField
                    value={opportunity.rep_firm_id ? String(opportunity.rep_firm_id) : ''}
                    onSave={(v) => saveField('rep_firm_id', v)}
                    canEdit={canDo('opportunities', 'edit')}
                    type="select"
                    options={[{ value: '', label: 'None' }, ...repFirms.map((rf) => ({ value: String(rf.id), label: rf.name }))]}
                    label="rep firm"
                    emptyText="Not set"
                  />
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Stage</span>
                <span className="detail-value">
                  <InlineField
                    value={opportunity.stage_id ? String(opportunity.stage_id) : ''}
                    onSave={(v) => saveField('stage_id', v)}
                    canEdit={canDo('opportunities', 'edit') && !opportunity.is_won && !opportunity.is_lost}
                    type="select"
                    options={[
                      { value: '', label: 'Select stage' },
                      ...stages.filter((s) => !['Order Received', 'Closed Won'].includes(s.name)).map((s) => ({ value: String(s.id), label: s.name })),
                      ...(opportunity.stage_name && ['Order Received', 'Closed Won'].includes(opportunity.stage_name) ? [{ value: String(opportunity.stage_id), label: opportunity.stage_name }] : []),
                    ]}
                    label="stage"
                    emptyText="Not set"
                    format={() => <span className={`badge ${opportunity.is_won ? 'badge-success' : opportunity.is_lost ? 'badge-danger' : 'badge-primary'}`}>{opportunity.stage_name}</span>}
                  />
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Amount</span>
                <span className="detail-value" style={{ color: 'var(--success)', fontWeight: 600 }}>
                  {formatCurrency(opportunity.amount || calculateTotal(opportunity.line_items))}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Expected Close Date</span>
                <span className="detail-value">
                  <InlineField
                    value={opportunity.expected_close_date ? String(opportunity.expected_close_date).slice(0, 10) : ''}
                    onSave={(v) => saveField('expected_close_date', v)}
                    canEdit={canDo('opportunities', 'edit')}
                    type="date"
                    label="expected close date"
                    emptyText="Not set"
                    format={(v) => new Date(`${v}T12:00:00`).toLocaleDateString()}
                  />
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">
                  {new Date(opportunity.created_at).toLocaleString()} by {opportunity.created_by_name || 'Unknown'}
                </span>
              </div>
              {opportunity.updated_by_name && (
                <div className="detail-row">
                  <span className="detail-label">Updated</span>
                  <span className="detail-value">
                    {new Date(opportunity.updated_at).toLocaleString()} by {opportunity.updated_by_name}
                  </span>
                </div>
              )}

              <div className="detail-row">
                <span className="detail-label">Notes</span>
                <span className="detail-value">
                  <InlineField value={opportunity.notes || ''} onSave={(v) => saveField('notes', v)} canEdit={canDo('opportunities', 'edit')} multiline label="notes" emptyText="No notes" />
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Summary</h3>
            </div>
            <div className="card-body">
              <div className="summary-stats">
                <div className="summary-stat">
                  <DollarSign size={24} className="summary-icon" />
                  <div>
                    <div className="summary-value">
                      {formatCurrency(opportunity.amount || calculateTotal(opportunity.line_items))}
                    </div>
                    <div className="summary-label">Opportunity Value</div>
                  </div>
                </div>
                <div className="summary-stat">
                  <FileText size={24} className="summary-icon" />
                  <div>
                    <div className="summary-value">{opportunity.quotes?.length || 0}</div>
                    <div className="summary-label">Quotes</div>
                  </div>
                </div>
                <div className="summary-stat">
                  <Target size={24} className="summary-icon" />
                  <div>
                    <div className="summary-value">{opportunity.orders?.length || 0}</div>
                    <div className="summary-label">Orders</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Products</h3>
            {canDo('opportunities', 'edit') && frozenFieldsEditable && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {isEditingProducts ? (
                  <>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={saveInlineProducts}
                      disabled={productsSaving}
                    >
                      <Check size={16} />
                      {productsSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={stopEditingProducts}
                      disabled={productsSaving}
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={startEditingProducts}
                    title="Edit products"
                  >
                    <Edit size={16} />
                    Edit Products
                  </button>
                )}
              </div>
            )}
          </div>
          {!frozenFieldsEditable && (
            <div style={{ padding: '8px 16px', background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-primary)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              <AlertTriangle size={14} />
              Products are locked while a quote is in Sent or Accepted status.
            </div>
          )}
          <div className="table-container">
            <table className="table" aria-label="Products list">
              <thead>
                <tr>
                  <th scope="col">Part #</th>
                  <th scope="col">Product</th>
                  <th scope="col">Description</th>
                  <th scope="col">Quantity</th>
                  <th scope="col">Unit Price</th>
                  <th scope="col">Discount</th>
                  <th scope="col">Total</th>
                  {isEditingProducts && <th scope="col" style={{ width: '50px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {isEditingProducts ? (
                  <>
                    {inlineLineItems.length > 0 ? (
                      <>
                        {inlineLineItems.map((item, index) => (
                          <tr key={index}>
                            <td>
                              {item.sku || item.product_sku || <EmptyValue label="—" />}
                            </td>
                            <td>
                              <select
                                className="inline-table-input"
                                value={item.product_id || ''}
                                onChange={(e) => updateInlineLineItem(index, 'product_id', e.target.value)}
                              >
                                <option value="">Select product...</option>
                                {products.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              {item.description || <EmptyValue label="—" />}
                            </td>
                            <td>
                              <input
                                type="number"
                                className="inline-table-input"
                                value={item.quantity}
                                min="0"
                                step="1"
                                onChange={(e) => updateInlineLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                style={{ width: '80px' }}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="inline-table-input"
                                value={item.unit_price}
                                min="0"
                                step="0.01"
                                onChange={(e) => updateInlineLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                disabled={!canOverridePrice()}
                                style={{ width: '110px' }}
                              />
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input
                                  type="number"
                                  className="inline-table-input"
                                  value={item.discount_value || 0}
                                  min="0"
                                  max={getMaxDiscountPct()}
                                  step="1"
                                  onChange={(e) => {
                                    if (!item.discount_type) updateInlineLineItem(index, 'discount_type', 'percentage');
                                    updateInlineLineItem(index, 'discount_value', parseFloat(e.target.value) || 0);
                                  }}
                                  style={{ width: '70px' }}
                                />
                                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>%</span>
                              </div>
                            </td>
                            <td>{formatCurrency(calculateLineTotal(item))}</td>
                            <td>
                              <button
                                className="btn-icon"
                                onClick={() => removeInlineLineItem(index)}
                                title="Remove product"
                                style={{ color: 'var(--accent-danger, #dc2626)' }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="table-footer">
                          <td colSpan="6" style={{ textAlign: 'right', fontWeight: 600 }}>Total:</td>
                          <td style={{ fontWeight: 600 }}>{formatCurrency(calculateTotal(inlineLineItems))}</td>
                          <td></td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center text-secondary">No products added</td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="8" style={{ borderTop: 'none' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={addInlineLineItem}
                          type="button"
                        >
                          <Plus size={16} />
                          Add Product
                        </button>
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    {opportunity.line_items?.length > 0 ? (
                      <>
                        {opportunity.line_items.map((item, index) => (
                          <tr
                            key={index}
                            style={{ cursor: canDo('opportunities', 'edit') && frozenFieldsEditable ? 'pointer' : undefined }}
                            onClick={() => {
                              if (canDo('opportunities', 'edit') && frozenFieldsEditable && !isEditingProducts) {
                                startEditingProducts();
                              }
                            }}
                          >
                            <td>{item.product_sku || item.sku || <EmptyValue label="No SKU" />}</td>
                            <td>{item.product_name}</td>
                            <td>{item.description || <EmptyValue label="No description" />}</td>
                            <td>{item.quantity}</td>
                            <td>{formatCurrency(item.unit_price)}</td>
                            <td style={{ color: item.discount_value > 0 ? 'var(--accent-danger, #dc2626)' : undefined }}>
                              {item.discount_type === 'percentage' && item.discount_value > 0 ? `${item.discount_value}%` : '-'}
                            </td>
                            <td>{formatCurrency(item.line_total)}</td>
                          </tr>
                        ))}
                        <tr className="table-footer">
                          <td colSpan="6" style={{ textAlign: 'right', fontWeight: 600 }}>Total:</td>
                          <td style={{ fontWeight: 600 }}>{formatCurrency(calculateTotal(opportunity.line_items))}</td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-secondary">No products added</td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quotes Tab */}
      {activeTab === 'quotes' && isModuleVisible('quotes') && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Quotes</h3>
            {canDo('quotes', 'create') && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleCreateQuote}
                disabled={hasActiveQuote}
                title={hasActiveQuote ? `Active quote exists: ${activeQuote.quote_number}` : ''}
              >
                <Plus size={16} />
                New Quote
              </button>
            )}
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Quote #</th>
                  <th scope="col">Total</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created</th>
                </tr>
              </thead>
              <tbody>
                {opportunity.quotes?.length > 0 ? (
                  opportunity.quotes.map(quote => (
                    <tr key={quote.id}>
                      <td>
                        <Link to={`/quotes/${quote.id}`} state={{ fromOpportunity: id, fromAccount: opportunity.account_id }}>{quote.quote_number}</Link>
                      </td>
                      <td>{formatCurrency(quote.total)}</td>
                      <td>
                        <span className={`badge badge-${getQuoteStatusColor(quote.status)}`}>
                          {quote.status}
                        </span>
                      </td>
                      <td>{new Date(quote.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-secondary">No quotes</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && isModuleVisible('orders') && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Orders</h3>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Order #</th>
                  <th scope="col">Total</th>
                  <th scope="col">Status</th>
                  <th scope="col">Order Date</th>
                </tr>
              </thead>
              <tbody>
                {opportunity.orders?.length > 0 ? (
                  opportunity.orders.map(order => (
                    <tr key={order.id}>
                      <td>
                        <Link to={`/orders/${order.id}`} state={{ fromOpportunity: id }}>{order.order_number}</Link>
                      </td>
                      <td>{formatCurrency(order.total)}</td>
                      <td>
                        <span className={`badge badge-${getOrderStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>{new Date(order.order_date).toLocaleDateString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-secondary">No orders</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && isModuleVisible('activities') && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Activities</h3>
          </div>
          <div className="card-body">
            {opportunity.activities?.length > 0 ? (
              <div className="activity-timeline">
                {opportunity.activities.map(activity => (
                  <div key={activity.id} className="timeline-item">
                    <div className={`timeline-icon timeline-icon-${activity.type.toLowerCase()}`}>
                      {activity.type === 'Call' && <Phone size={14} />}
                      {activity.type === 'Email' && <Mail size={14} />}
                      {activity.type === 'Meeting' && <Calendar size={14} />}
                      {activity.type === 'Note' && <FileText size={14} />}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-type">{activity.type}</span>
                        <span className="timeline-date">
                          {new Date(activity.activity_date).toLocaleString()}
                        </span>
                      </div>
                      {activity.contact_name && (
                        <div className="timeline-contact">With: {activity.contact_name}</div>
                      )}
                      {activity.description && (
                        <div className="timeline-description"><BoldText text={activity.description} /></div>
                      )}
                      <div className="timeline-logged-by">
                        by {activity.logged_by_name}
                        {activity.updated_by_name && (
                          <span style={{ marginLeft: '8px', color: 'var(--text-tertiary)', fontSize: '12px' }}>
                            · Edited by {activity.updated_by_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-secondary text-center">No activities logged</p>
            )}
          </div>
        </div>
      )}

      {/* Action Items Tab */}
      {activeTab === 'tasks' && isModuleVisible('tasks') && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Action Items</h3>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Action Item</th>
                  <th scope="col">Due Date</th>
                  <th scope="col">Assigned To</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {opportunity.tasks?.length > 0 ? (
                  opportunity.tasks.map(task => (
                    <tr key={task.id}>
                      <td>{task.title}</td>
                      <td>
                        {task.due_date ? (
                          <span className={new Date(task.due_date) < new Date() && task.status === 'Open' ? 'text-danger' : ''}>
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        ) : '-'}
                      </td>
                      <td>{task.assigned_to_name || '-'}</td>
                      <td>
                        <span className={`badge ${task.status === 'Completed' ? 'badge-success' : 'badge-warning'}`}>
                          {task.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-secondary">No action items</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal removed — all fields are now inline editable */}

      {/* Log Activity Modal */}
      <Modal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        title="Log Activity"
      >
        <form onSubmit={handleLogActivity}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Type *</label>
              <select
                className="form-select"
                value={activityForm.type}
                onChange={(e) => setActivityForm({ ...activityForm, type: e.target.value })}
                required
              >
                <option value="Call">Call</option>
                <option value="Email">Email</option>
                <option value="Meeting">Meeting</option>
                <option value="Note">Note</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={activityForm.description}
                onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                placeholder="Add notes about this activity..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowActivityModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Log Activity
            </button>
          </div>
        </form>
      </Modal>

      {/* Create Action Item Modal */}
      <Modal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        title="Add Action Item"
      >
        <form onSubmit={handleCreateTask}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Action Item *</label>
              <input
                type="text"
                className="form-input"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="datetime-local"
                className="form-input"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Assign To</label>
              <select
                className="form-select"
                value={taskForm.assigned_to}
                onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
              >
                <option value="">Assign to me</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowTaskModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create Action Item
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Opportunity"
        message={`Are you sure you want to delete "${opportunity.name}"? This action cannot be undone.`}
        confirmText="Delete"
      />

      {/* Stage Gate Modal removed — validation is now product/discount based */}

      {/* Create Contact Modal */}
      <Modal isOpen={showNewContactModal} onClose={() => setShowNewContactModal(false)} title="Create Contact">
        <form onSubmit={handleCreateContact}>
          <div className="modal-body">
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input type="text" className="form-input" value={newContactForm.first_name} onChange={(e) => setNewContactForm({ ...newContactForm, first_name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input type="text" className="form-input" value={newContactForm.last_name} onChange={(e) => setNewContactForm({ ...newContactForm, last_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={newContactForm.email} onChange={(e) => setNewContactForm({ ...newContactForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={newContactForm.phone} onChange={(e) => setNewContactForm({ ...newContactForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Job Title</label>
              <input type="text" className="form-input" value={newContactForm.job_title} onChange={(e) => setNewContactForm({ ...newContactForm, job_title: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewContactModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Contact</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function getQuoteStatusColor(status) {
  switch (status) {
    case 'Draft': return 'gray';
    case 'Sent': return 'primary';
    case 'Accepted': return 'success';
    case 'Rejected': return 'danger';
    default: return 'gray';
  }
}

function getOrderStatusColor(status) {
  switch (status) {
    case 'Order Created': return 'warning';
    case 'Shipped': return 'success';
    default: return 'gray';
  }
}

export default OpportunityDetail;
